import { getAuth } from 'firebase/auth';
import type { UserHealthProfile, HealthGoal, Treatment } from '@researcher/shared';
import { apiUrl } from '../config/api';

async function getHeaders(): Promise<HeadersInit> {
  const auth = getAuth();
  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : null;

  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

// ─── Profile + Goals ────────────────────────────────────────────────

export async function getHealthProfile(): Promise<{
  healthProfile: UserHealthProfile | null;
  healthGoals: HealthGoal[];
}> {
  const headers = await getHeaders();
  const response = await fetch(apiUrl('/health/profile'), { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch health profile');
  }

  const data = await response.json();
  return data.data;
}

export async function updateHealthProfile(profile: Partial<Omit<UserHealthProfile, 'updatedAt'>>): Promise<UserHealthProfile> {
  const headers = await getHeaders();
  const response = await fetch(apiUrl('/health/profile'), {
    method: 'PUT',
    headers,
    body: JSON.stringify(profile),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to update health profile');
  }

  const data = await response.json();
  return data.data;
}

export async function updateHealthGoals(goals: HealthGoal[]): Promise<HealthGoal[]> {
  const headers = await getHeaders();
  const response = await fetch(apiUrl('/health/goals'), {
    method: 'PUT',
    headers,
    body: JSON.stringify({ goals }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to update health goals');
  }

  const data = await response.json();
  return data.data;
}

// ─── Treatments ─────────────────────────────────────────────────────

export async function getTreatments(status?: string): Promise<Treatment[]> {
  const headers = await getHeaders();
  const params = new URLSearchParams();
  if (status) params.set('status', status);

  const response = await fetch(`${apiUrl('/health/treatments')}?${params.toString()}`, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch treatments');
  }

  const data = await response.json();
  return data.data;
}

export async function createTreatment(treatment: Partial<Treatment>): Promise<Treatment> {
  const headers = await getHeaders();
  const response = await fetch(apiUrl('/health/treatments'), {
    method: 'POST',
    headers,
    body: JSON.stringify(treatment),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to create treatment');
  }

  const data = await response.json();
  return data.data;
}

export async function updateTreatment(id: string, updates: Partial<Treatment>): Promise<Treatment> {
  const headers = await getHeaders();
  const response = await fetch(apiUrl(`/health/treatments/${id}`), {
    method: 'PUT',
    headers,
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to update treatment');
  }

  const data = await response.json();
  return data.data;
}

export async function deleteTreatment(id: string): Promise<void> {
  const headers = await getHeaders();
  const response = await fetch(apiUrl(`/health/treatments/${id}`), {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to delete treatment');
  }
}
